import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useAuth } from "@theg1group/rbac-ui";
const DEFAULT_AUTH_URL = "https://auth.g1.network";
function redirect(url) {
    if (typeof window !== "undefined")
        window.location.replace(url);
}
function ShellMessage({ label }) {
    return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-canvas", children: _jsx("p", { className: "text-muted text-sm", children: label }) }));
}
const DEFAULT_API_URL = "https://api.g1.network";
/**
 * Shown when a signed-in user is denied an app (or, later, a permission). Instead
 * of a dead end, lets them request access — POST /access-requests to shared-api —
 * which lands in the admin.g1.network AccessRequests queue. The humane counterpart
 * to fail-closed gates (2026-09-14 access-control audit).
 */
export function NoAccess({ app, permission, apiBase = DEFAULT_API_URL, }) {
    const [reason, setReason] = useState("");
    const [state, setState] = useState("idle");
    const label = permission ?? app ?? "this resource";
    // On mount, check whether the user already has a pending request for this
    // exact app/permission — so a reload of the block page shows "already
    // requested" instead of a fresh form the user re-submits. Backend dedups
    // anyway (one open row per user+app+permission), this just reflects it in UI.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${apiBase}/access-requests/mine`, {
                    credentials: "include",
                });
                if (!res.ok)
                    return;
                const body = (await res.json().catch(() => ({})));
                const pending = (body.data ?? []).some((r) => r.status === "pending" &&
                    (permission
                        ? r.requestedPermission === permission
                        : r.requestedApp === app));
                // Only upgrade idle → pending; never clobber a live submit/error.
                if (!cancelled && pending)
                    setState((s) => (s === "idle" ? "pending" : s));
            }
            catch {
                /* offline / error — leave the form usable */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [apiBase, app, permission]);
    async function requestAccess() {
        setState("submitting");
        try {
            const res = await fetch(`${apiBase}/access-requests`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ app, permission, reason: reason.trim() || undefined }),
            });
            if (!res.ok)
                throw new Error(String(res.status));
            const body = (await res.json().catch(() => ({})));
            setState(body.status === "already_pending" ? "pending" : "submitted");
        }
        catch {
            setState("error");
        }
    }
    return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-canvas p-8", children: _jsxs("div", { className: "w-full max-w-md text-center", children: [_jsx("h1", { className: "mb-2 text-xl font-bold text-content", children: "Access needed" }), _jsxs("p", { className: "mb-4 text-sm text-muted", children: ["Your account doesn't have access to ", _jsx("strong", { children: label }), ". You can request it below \u2014 an admin will review."] }), state === "submitted" || state === "pending" ? (_jsx("p", { className: "rounded-lg border border-line bg-surface p-4 text-sm text-content", children: state === "pending"
                        ? "You already have a pending request for this — an admin will review it."
                        : "Request sent. You'll get access once an admin approves it." })) : (_jsxs("div", { className: "space-y-3", children: [_jsx("textarea", { value: reason, onChange: (e) => setReason(e.target.value), placeholder: "Why do you need access? (optional)", rows: 3, className: "w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content focus:border-primary focus:outline-none" }), _jsx("button", { type: "button", onClick: requestAccess, disabled: state === "submitting", className: "w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50", children: state === "submitting" ? "Sending…" : "Request access" }), state === "error" && (_jsx("p", { className: "text-sm text-danger", children: "Couldn't send the request \u2014 please try again." }))] }))] }) }));
}
/**
 * The auth gate every operator app copy-pastes today, lifted into the shell.
 * Resolves the session from rbac-ui's `AuthProvider` (which the app mounts with
 * its own `fetchUser`), bouncing to `auth.g1.network` on no session and to 2FA
 * setup when unenrolled. Renders children only for a fully-authenticated user.
 *
 * Cookies are `Domain=.g1.network`, so the return trip re-establishes SSO.
 */
export function RequireAuth({ authUrl = DEFAULT_AUTH_URL, apiBase = DEFAULT_API_URL, require2FA = true, requireAppAccess, noAccessFallback, loadingFallback, children, }) {
    const { user, isLoading } = useAuth();
    if (isLoading) {
        return _jsx(_Fragment, { children: loadingFallback ?? _jsx(ShellMessage, { label: "Loading\u2026" }) });
    }
    if (!user) {
        const ret = typeof window !== "undefined" ? encodeURIComponent(window.location.href) : "";
        redirect(`${authUrl}/?return=${ret}`);
        return _jsx(ShellMessage, { label: "Redirecting to sign in\u2026" });
    }
    if (require2FA && user.totpEnabled === false) {
        const ret = typeof window !== "undefined" ? encodeURIComponent(window.location.href) : "";
        redirect(`${authUrl}/?setup=1&return=${ret}`);
        return _jsx(ShellMessage, { label: "Redirecting to 2FA setup\u2026" });
    }
    if (requireAppAccess) {
        const access = user.appAccess ?? [];
        const ok = user.role === "admin" || access.includes("*") || access.includes(requireAppAccess);
        if (!ok)
            return _jsx(_Fragment, { children: noAccessFallback ?? _jsx(NoAccess, { app: requireAppAccess, apiBase: apiBase }) });
    }
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=RequireAuth.js.map